// Z86: supplier (cost) invoices -- types + thin wrappers over the z86_* database functions.
// All the rules (warnings, over-allocation, accumulate-not-overwrite, audit trail) live in the
// database functions so the UI and the server can never disagree; this file only calls them.
import { supabase } from '../api/supabase'

export type SupplierInvoiceStatus = 'UNALLOCATED' | 'PARTIAL' | 'ALLOCATED'

export interface SupplierInvoice {
  id: string
  supplier_id: string
  invoice_no: string
  invoice_date: string
  currency: string
  exch_rate: number
  net_amount: number
  vat_amount: number
  total_amount: number
  status: SupplierInvoiceStatus
  notes: string | null
  attachment_path: string | null
  created_at: string
  supplier?: { id: string; code: string; name: string } | null
  allocations?: { amount: number }[]
  acks?: { id: string }[]
}

export interface Warning {
  key: string
  type: string
  severity: 'warn' | 'strong'
  requires_reason: boolean
  job_charge_id: string | null
  message: string
  detail: unknown
}

export interface Decision { job_charge_id: string; manual_amount: number; currency: string }
export interface AckInput { key: string; reason?: string }
export interface AllocInput { job_charge_id: string; amount: number; notes?: string }
export interface DecisionInput { job_charge_id: string; action: 'replace' | 'keep' }

export interface RpcFailure {
  ok: false
  error: string
  message?: string
  warnings?: Warning[]
  decisions?: Decision[]
}
export type RpcResult<T = Record<string, never>> = ({ ok: true } & T) | RpcFailure

export const r2 = (n: number) => Math.round(n * 100) / 100
export const allocatedNet = (inv: Pick<SupplierInvoice, 'allocations'>) =>
  r2((inv.allocations ?? []).reduce((s, a) => s + Number(a.amount), 0))

export const WARNING_LABEL: Record<string, string> = {
  CHARGE_ALREADY_ALLOCATED: 'Already allocated',
  SAME_SUPPLIER_JOB_CODE: 'Same supplier / job / charge code',
  OVER_ESTIMATE: 'Over estimate',
  DUPLICATE_INVOICE_NO: 'Duplicate invoice number',
  CURRENCY_MISMATCH: 'Currency differs from cost line',
  SUPPLIER_MISMATCH: 'Different supplier on cost line',
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args)
  if (error) throw new Error(error.message)
  return data as T
}

export async function detectWarnings(a: {
  supplierId: string; invoiceNo: string | null; currency: string; exchRate: number
  invoiceId: string | null; allocs: AllocInput[]
}): Promise<{ warnings: Warning[]; decisions: Decision[] }> {
  return rpc('z86_detect_warnings', {
    p_supplier_id: a.supplierId, p_invoice_no: a.invoiceNo, p_currency: a.currency,
    p_exch_rate: a.exchRate, p_invoice_id: a.invoiceId, p_allocs: a.allocs,
  })
}

export function createSupplierInvoice(header: Record<string, unknown>, acks: AckInput[]) {
  return rpc<RpcResult<{ invoice_id: string }>>('z86_create_supplier_invoice', { p_header: header, p_acks: acks })
}

export function saveAllocations(invoiceId: string, allocs: AllocInput[], acks: AckInput[], decisions: DecisionInput[]) {
  return rpc<RpcResult<{ status: SupplierInvoiceStatus; allocated: number; net: number }>>('z86_save_allocations', {
    p_invoice_id: invoiceId, p_allocs: allocs, p_acks: acks, p_decisions: decisions,
  })
}

export function deleteAllocation(allocationId: string) {
  return rpc<RpcResult<{ status: SupplierInvoiceStatus }>>('z86_delete_allocation', { p_allocation_id: allocationId })
}

export interface InvoiceListFilters { status?: string; supplierId?: string; invoiceNo?: string }

export async function fetchSupplierInvoices(f: InvoiceListFilters): Promise<SupplierInvoice[]> {
  let q = supabase
    .from('supplier_invoices')
    .select('*, supplier:customers!supplier_id(id,code,name), allocations:supplier_invoice_allocations(amount), acks:supplier_invoice_warning_acks(id)')
    .order('invoice_date', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(200)
  if (f.status) q = q.eq('status', f.status)
  if (f.supplierId) q = q.eq('supplier_id', f.supplierId)
  const t = (f.invoiceNo ?? '').trim().replace(/[%,()]/g, ' ')
  if (t) q = q.ilike('invoice_no', `%${t}%`)
  const { data, error } = await q
  if (error) throw new Error(error.message)
  return (data ?? []) as unknown as SupplierInvoice[]
}

// Server-side job search for the allocation picker (never loads all jobs).
export async function searchJobs(term: string): Promise<{ id: string; job_ref: string }[]> {
  const t = term.trim().replace(/[%,()]/g, ' ')
  let q = supabase.from('jobs').select('id, job_ref').order('job_ref', { ascending: false }).limit(30)
  if (t) q = q.ilike('job_ref', `%${t}%`)
  const { data, error } = await q
  if (error) throw new Error(error.message)
  return data ?? []
}

export interface CostLine {
  id: string
  description: string | null
  charge_code_id: string | null
  est_amount: number | null
  act_amount: number | null
  cost_currency: string
  cost_supplier_id: string | null
  cost_supplier: { name: string } | null
}

export async function fetchCostLines(jobId: string): Promise<CostLine[]> {
  const { data, error } = await supabase
    .from('job_charges')
    .select('id, description, charge_code_id, est_amount, act_amount, cost_currency, cost_supplier_id, cost_supplier:customers!cost_supplier_id(name)')
    .eq('job_id', jobId).eq('include_in_cost', true).order('created_at')
  if (error) throw new Error(error.message)
  return (data ?? []) as unknown as CostLine[]
}

// Per-warning tick state used by the dialogs: every warning must be ticked individually; only the
// duplicate-invoice-number warning also forces a reason.
export type AckState = Record<string, { checked: boolean; reason: string }>

export const allAcked = (warnings: Warning[], state: AckState) =>
  warnings.every(w => {
    const a = state[w.key]
    return !!a?.checked && (!w.requires_reason || a.reason.trim().length > 0)
  })

export const toAckInputs = (warnings: Warning[], state: AckState) =>
  warnings.map(w => ({ key: w.key, reason: state[w.key]?.reason?.trim() || undefined }))

